import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AlertMessage, AlertState } from "../types";

// Generate unique message ID
const generateMessageId = (): string => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const createMessage = (
  message: string,
  type: "success" | "error" | "warning",
  options?: Partial<AlertMessage>
): AlertMessage => {
  return {
    id: generateMessageId(),
    message,
    type,
    timestamp: Date.now(),
    dismissible: true,
    autoDismiss: true,
    dismissAfter: 5000,
    ...options,
  };
};

const initialState: AlertState = {
  messages: [],
  isProcessing: false,
};

const alertsSlice = createSlice({
  name: "alerts",
  initialState,
  reducers: {
    // Add success message
    addSuccess: (state, action: PayloadAction<string>) => {
      const message = createMessage(action.payload, "success");
      state.messages.push(message);
      state.isProcessing = false;
    },

    // Add error message
    addError: (state, action: PayloadAction<string>) => {
      const message = createMessage(action.payload, "error");
      state.messages.push(message);
      state.isProcessing = false;
    },

    // Add warning message
    addWarning: (state, action: PayloadAction<string>) => {
      const message = createMessage(action.payload, "warning");
      state.messages.push(message);
      state.isProcessing = false;
    },

    // Add custom message
    addMessage: (state, action: PayloadAction<AlertMessage>) => {
      state.messages.push(action.payload);
      state.isProcessing = false;
    },

    // Remove specific message
    removeMessage: (state, action: PayloadAction<string>) => {
      const messageId = action.payload;
      state.messages = state.messages.filter((msg) => msg.id !== messageId);
    },

    // Clear all messages
    clearMessages: (state) => {
      state.messages = [];
    },

    // Clear messages by type
    clearMessagesByType: (
      state,
      action: PayloadAction<"success" | "error" | "warning">
    ) => {
      const type = action.payload;
      state.messages = state.messages.filter((msg) => msg.type !== type);
    },

    // Set processing state
    setProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },
  },
});

export const {
  addSuccess,
  addError,
  addWarning,
  addMessage,
  removeMessage,
  clearMessages,
  clearMessagesByType,
  setProcessing,
} = alertsSlice.actions;

export const selectAllMessages = (state: { alerts: AlertState }) =>
  state.alerts.messages;
export const selectSuccessMessages = (state: { alerts: AlertState }) =>
  state.alerts.messages.filter((msg) => msg.type === "success");
export const selectErrorMessages = (state: { alerts: AlertState }) =>
  state.alerts.messages.filter((msg) => msg.type === "error");
export const selectIsProcessing = (state: { alerts: AlertState }) =>
  state.alerts.isProcessing;
export const selectMessageCount = (state: { alerts: AlertState }) =>
  state.alerts.messages.length;

export default alertsSlice.reducer;
